import React from 'react';
import { bool, shape, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';
import { withRouter } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import googleHomeLogo from '../../assets/google-home-logo.png';
import alexaLogo from '../../assets/alexa-logo.png';
import appleHomekitLogo from '../../assets/apple-homekit-logo.png.png';
import homeassistantLogo from '../../assets/home-assistant-logo.png';
import smartthingsLogo from '../../assets/smartthings-logo.png';

import css from './CompatibilityPage.module.css';

// Ecosystems data - matching CompatibilityMegaMenu
const ecosystemsData = [
  {
    name: "Google Home",
    value: "googlehome",
    description: "Écosystème Google Assistant",
    icon: googleHomeLogo,
    isImage: true,
    color: "#4285F4",
    url: "/compatibility/google-home"
  },
  {
    name: "Alexa",
    value: "alexa",
    description: "Écosystème Amazon Alexa",
    icon: alexaLogo,
    isImage: true,
    color: "#FF9900",
    url: "/compatibility/alexa"
  },
  {
    name: "Apple HomeKit",
    value: "homekit",
    description: "Écosystème Apple Home",
    icon: appleHomekitLogo,
    isImage: true,
    color: "#007AFF",
    url: "/compatibility/homekit"
  },
  {
    name: "Home Assistant",
    value: "homeassistant",
    description: "Automatisation domestique open source",
    icon: homeassistantLogo,
    isImage: true,
    color: "#41BDF5",
    url: "/compatibility/home-assistant"
  },
  {
    name: "SmartThings",
    value: "smartthings",
    description: "Plateforme IoT Samsung",
    icon: smartthingsLogo,
    isImage: true,
    color: "#1428A0",
    url: "/compatibility/smartthings"
  }
];

// Network connectivity data - matching CompatibilityMegaMenu
const connectivityData = [
  {
    name: "Wi-Fi",
    value: "wifi",
    description: "Connectivité internet sans fil",
    icon: "📶",
    color: "#00C851",
    url: "/compatibility/wifi"
  },
  {
    name: "Zigbee",
    value: "zigbee",
    description: "Réseau maillé sans fil basse consommation",
    icon: "🕷️",
    color: "#FF6B6B",
    url: "/compatibility/zigbee"
  },
  {
    name: "Matter",
    value: "matter",
    description: "Standard domotique unifié",
    icon: "🔗",
    color: "#45B7D1",
    url: "/compatibility/matter"
  },
  {
    name: "Bluetooth",
    value: "bluetooth",
    description: "Technologie sans fil courte portée",
    icon: "📡",
    color: "#2E86AB",
    url: "/compatibility/bluetooth"
  },
  {
    name: "Thread",
    value: "thread",
    description: "Réseau maillé basé sur IPv6",
    icon: "🧵",
    color: "#A23B72",
    url: "/compatibility/thread"
  },
  {
    name: "Cellular/LTE",
    value: "cellular",
    description: "Connectivité réseau mobile",
    icon: "📱",
    color: "#9C27B0",
    url: "/compatibility/cellular"
  },
  {
    name: "Ethernet",
    value: "ethernet",
    description: "Connexion réseau filaire",
    icon: "🔌",
    color: "#4CAF50",
    url: "/compatibility/ethernet"
  }
];

export const CompatibilityPageComponent = props => {
  const { inProgress, error, history } = props;

  if (inProgress) {
    return (
      <Page
        title="Compatibilité Domotique | Neokasa"
        description="Découvrez quels appareils domotiques fonctionnent avec Google Home, Alexa, HomeKit, SmartThings, Matter, Zigbee, Z-Wave et plus encore."
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.loading}>Chargement des informations de compatibilité...</div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (error) {
    return (
      <Page
        title="Compatibilité Domotique | Neokasa"
        description="Découvrez quels appareils domotiques fonctionnent avec Google Home, Alexa, HomeKit, SmartThings, Matter, Zigbee, Z-Wave et plus encore."
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.error}>
              <h2>Oups, une erreur s'est produite !</h2>
              <p>{error.message}</p>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const handleItemClick = (item, type) => {
    if (!history) {
      return;
    }

    if (type === 'ecosystem' && item.value) {
      history.push(`/products?compatibility=${item.value}`);
      return;
    }

    if (type === 'connectivity' && item.value) {
      history.push(`/products?connectivity=has_any:${item.value}`);
      return;
    }

    if (item.url) {
      history.push(item.url);
    }
  };

  return (
    <Page
      title="Compatibilité Domotique | Neokasa"
      description="Explorez les écosystèmes et protocoles de connectivité comme Matter, Zigbee, Z-Wave et Thread pour garantir que vos appareils domotiques fonctionnent ensemble."
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <h1 className={css.title}>Compatibilité Domotique</h1>
            <p className={css.subtitle}>Explorez les écosystèmes et options de connectivité pour votre maison intelligente</p>

            {/* Ecosystems Section */}
            <section className={css.section}>
              <h2 className={css.sectionTitle}>Écosystèmes Domotiques</h2>
              <p className={css.sectionDescription}>
                Choisissez votre écosystème domotique préféré pour contrôler et gérer vos appareils
              </p>
              <div className={css.grid}>
                {ecosystemsData.map((ecosystem, index) => (
                  <div
                    key={index}
                    className={css.card}
                    onClick={() => handleItemClick(ecosystem, 'ecosystem')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(ecosystem, 'ecosystem');
                      }
                    }}
                  >
                    <div className={css.ecosystemIconContainer}>
                      {ecosystem.isImage ? (
                        <img src={ecosystem.icon} alt={ecosystem.name} className={css.ecosystemIconImage} />
                      ) : (
                        <span className={css.icon}>{ecosystem.icon}</span>
                      )}
                    </div>
                    <h3 className={css.cardTitle}>{ecosystem.name}</h3>
                  </div>
                ))}
              </div>
            </section>

            {/* Connectivity Section */}
            <section className={css.section}>
              <h2 className={css.sectionTitle}>Connectivité Réseau</h2>
              <p className={css.sectionDescription}>
                Comprenez les différents protocoles sans fil et options de connectivité
              </p>
              <div className={css.grid}>
                {connectivityData.map((connectivity, index) => (
                  <div
                    key={index}
                    className={css.card}
                    onClick={() => handleItemClick(connectivity, 'connectivity')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(connectivity, 'connectivity');
                      }
                    }}
                  >
                    <div className={css.iconContainer} style={{ backgroundColor: connectivity.color + '20' }}>
                      <span className={css.icon}>{connectivity.icon}</span>
                    </div>
                    <h3 className={css.cardTitle}>{connectivity.name}</h3>
                    <p className={css.cardDescription}>{connectivity.description}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

CompatibilityPageComponent.propTypes = {
  inProgress: bool,
  error: propTypes.error,
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  // In a real app, you would fetch compatibility data from Redux state
  return {
    inProgress: false,
    error: null,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CompatibilityPage = compose(connect(mapStateToProps), withRouter)(CompatibilityPageComponent);

export default CompatibilityPage;
